import { createSelector } from "reselect"
import { get, first, last, chain, size, some } from "lodash"
import { getStartDate, getEndDate, getSelectedDate } from "./dates"
import { aggregateFactorsSumBy } from "helpers/factorClassification/index"
import { computeFactorCategories } from "helpers/factors"
import { computeAttributionMethod } from "helpers/risk"
import { computeMCTEAdjustedBase, computeMCTEAdjustedSegment } from "helpers/segments"
import { getBase, getIsActive, getSegment } from "."

const EMPTY_OBJECT = {}

export const getRiskRaw = state => get(state, "global.data.analyzeCompositeRisk.data.model.simulation.risk", EMPTY_OBJECT)
const getSelectedViews = (_, props) => props.selectedViews
const getSelectedViewsFactors = (_, props) => props.selectedViewsFactors
const getMustAggregateFactors = (_, props) => props.mustAggregateFactors
const getFactorsClassification = (_, props) => props.factorsClassification

export const getRisk = createSelector(
  [getRiskRaw, getMustAggregateFactors, getSelectedViews, getSelectedViewsFactors, getFactorsClassification],
  (riskRaw, mustAggregateFactors, selectedViews, selectedViewsFactors, factorsClassification) => chain(riskRaw)
    .map(item => {
      if (!item.total) {
        return null
      }
      let factors = item.attribution.factors
      if (mustAggregateFactors) {
        factors = aggregateFactorsSumBy(factorsClassification, factors, "value")
      }

      if (size(selectedViews)) {
        factors = chain(item.attribution.factors)
          .filter(factor => some(selectedViewsFactors, item => item.id === factor.id))
          .map(factor => ({ ...factor, customView: true }))
          .concat(factors)
          .value()
      }

      return {
        date: item.date,
        total: item.total,
        factorsTotal: item.attribution.summary.factors,
        specific: item.attribution.summary.specific,
        factors: chain(factors)
          .keyBy("id")
          .mapValues(f => f.value)
          .value(),
        categories: computeFactorCategories(factors, selectedViews),
      }
    })
    .filter(data => data)
    .value(),
)

export const getFilteredRisk = createSelector(
  [getRisk, getStartDate, getEndDate],
  (risk, startDate, endDate) =>
    risk.filter((data) => {
      if (data.date >= startDate && data.date <= endDate) {
        return data
      } else {
        return null
      }
    }),
)

export const getFirst = createSelector(
  [getFilteredRisk],
  (risk) => first(risk),
)

export const getCurrent = createSelector(
  [getRisk],
  (risk) => last(risk),
)

export const getFirstRiskDate = createSelector(
  [getFilteredRisk],
  (risk) => get(first(risk), "date", null),
)

export const getLastRiskDate = createSelector(
  [getFilteredRisk],
  (risk) => get(last(risk), "date", null),
)

export const getRiskSelectedDate = createSelector(
  [getLastRiskDate, getSelectedDate],
  (lastRiskDate, selectedDate) => {
    if (selectedDate > lastRiskDate) {
      return lastRiskDate
    }
    return selectedDate
  },
)

export const getAttributionMethod = createSelector(
  [getIsActive],
  computeAttributionMethod,
)

export const getMCTEAdjustedSegment = createSelector(
  [getSegment, getAttributionMethod, getIsActive],
  computeMCTEAdjustedSegment,
)

export const getMCTEAdjustedBase = createSelector(
  [getBase, getAttributionMethod, getIsActive],
  computeMCTEAdjustedBase,
)
